import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import { FaRedo } from 'react-icons/fa';
import styles from './styles/ClassroomCalendar.module.css';
import CalendarFilterBar from './CalendarFilterBar';
import CalendarEventContent from './CalendarEventContent';
import CalendarLegend from './CalendarLegend';
import useCalendarEvents from './useCalendarEvents';
import useNow from '../../../hooks/useNow';
import useIsMobile from '../../../utils/useIsMobile';
import { toLocalDateKey } from '../../../utils/dateUtils';
import {
    assignClassroomColors,
    getEventDestination,
    parseClassroomFilter,
    toCalendarEvent,
    withClassroomFilter,
} from './calendarUtils';

const VIEWS = ['dayGridMonth', 'dayGridWeek', 'listMonth'];
const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

const HEADER_TOOLBAR = { left: 'prev,next today', center: 'title', right: 'dayGridMonth,dayGridWeek,listMonth' };
const BUTTON_TEXT = { today: 'Today', month: 'Month', week: 'Week', list: 'List' };
const PLUGINS = [dayGridPlugin, listPlugin];

const renderEventContent = (arg) => (
    <CalendarEventContent event={arg.event} compact={!arg.view.type.startsWith('list')} />
);

/**
 * The global calendar: every assignment from every classroom the user belongs to.
 *
 * The URL is the source of truth for what's on screen — `classrooms` (the filter), `view`
 * and `date` — so a classroom can link straight to its own slice, and Back from an
 * assignment returns to the same month.
 */
const ClassroomCalendar = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const now = useNow();
    const { classrooms, events, truncated, loaded, loading, error, setRange, refetch } = useCalendarEvents();

    // FullCalendar only reads its initial view and date when it mounts.
    const [initial] = useState(() => {
        const view = searchParams.get('view');
        const date = searchParams.get('date');
        return {
            view: VIEWS.includes(view) ? view : (isMobile ? 'listMonth' : 'dayGridMonth'),
            date: date && DATE_KEY.test(date) ? date : undefined,
        };
    });

    // Read by FullCalendar's callbacks, which can fire from its own effects — before any
    // effect of ours would have run — so it's kept current during render.
    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;

    const classroomById = useMemo(
        () => new Map(classrooms.map((c) => [String(c._id), c])),
        [classrooms]
    );
    const colorById = useMemo(() => assignClassroomColors(classrooms), [classrooms]);

    const selectedIds = useMemo(() => parseClassroomFilter(searchParams), [searchParams]);
    // Ids for classrooms the user isn't in (left since, or a hand-edited URL) are ignored —
    // the filter is applied here, to data the server already scoped to this user.
    const activeIds = useMemo(
        () => selectedIds.filter((id) => classroomById.has(id)),
        [selectedIds, classroomById]
    );
    const filterIsStale = loaded && selectedIds.length > 0 && activeIds.length === 0;

    const visibleEvents = useMemo(() => {
        const filtering = selectedIds.length > 0;
        const allowed = new Set(activeIds);
        return events
            .filter((event) => !filtering || allowed.has(String(event.classroom)))
            .map((event) => toCalendarEvent(event, { colorById, classroomById, now }));
    }, [events, selectedIds, activeIds, colorById, classroomById, now]);

    const updateSearchParams = useCallback((next) => {
        if (next.toString() !== searchParamsRef.current.toString()) {
            setSearchParams(next, { replace: true });
        }
    }, [setSearchParams]);

    const handleToggleClassroom = useCallback((id) => {
        const next = activeIds.includes(id) ? activeIds.filter((x) => x !== id) : [...activeIds, id];
        updateSearchParams(withClassroomFilter(searchParamsRef.current, next));
    }, [activeIds, updateSearchParams]);

    const handleClearFilters = useCallback(() => {
        updateSearchParams(withClassroomFilter(searchParamsRef.current, []));
    }, [updateSearchParams]);

    const handleDatesSet = useCallback((info) => {
        setRange({ start: info.start, end: info.end });

        const next = new URLSearchParams(searchParamsRef.current);
        next.set('view', info.view.type);
        next.set('date', toLocalDateKey(info.view.currentStart));
        updateSearchParams(next);
    }, [setRange, updateSearchParams]);

    const handleEventClick = useCallback((info) => {
        info.jsEvent.preventDefault();
        const { pathname, state } = getEventDestination(info.event.extendedProps);
        navigate(pathname, { state });
    }, [navigate]);

    const isEmpty = loaded && !error && !filterIsStale && visibleEvents.length === 0;

    return (
        <div className={styles.page}>
            <CalendarFilterBar
                classrooms={classrooms}
                colorById={colorById}
                selectedIds={activeIds}
                onToggle={handleToggleClassroom}
                onClear={handleClearFilters}
                showClear={selectedIds.length > 0}
            />

            {error && (
                <div className={styles.errorBanner} role="alert">
                    <span>{error}</span>
                    <button type="button" className={styles.bannerButton} onClick={refetch}>
                        <FaRedo aria-hidden="true" /> Retry
                    </button>
                </div>
            )}

            {filterIsStale && (
                <div className={styles.notice} role="status">
                    <span>You&apos;re no longer in the classroom this calendar was filtered to.</span>
                    <button type="button" className={styles.bannerButton} onClick={handleClearFilters}>
                        Clear filters
                    </button>
                </div>
            )}

            {truncated && (
                <div className={styles.notice} role="status">
                    Some assignments in this range aren&apos;t shown — there are too many. Switch to the week
                    view or filter by classroom.
                </div>
            )}

            {isEmpty && (
                <div className={styles.emptyNote} role="status">No assignments in this period.</div>
            )}

            <div className={`${styles.calendarRoot} ${loading ? styles.loading : ''}`} aria-busy={loading}>
                <FullCalendar
                    plugins={PLUGINS}
                    initialView={initial.view}
                    initialDate={initial.date}
                    headerToolbar={HEADER_TOOLBAR}
                    buttonText={BUTTON_TEXT}
                    events={visibleEvents}
                    datesSet={handleDatesSet}
                    eventClick={handleEventClick}
                    eventContent={renderEventContent}
                    eventOrder="sortKey,title"
                    eventBackgroundColor="transparent"
                    eventBorderColor="transparent"
                    eventTextColor="inherit"
                    eventInteractive
                    dayMaxEvents={3}
                    fixedWeekCount={false}
                    height="auto"
                    noEventsContent=""
                />
            </div>

            {/* <CalendarLegend /> */}
        </div>
    );
};

export default ClassroomCalendar;
